import { useEffect } from 'react'
import { Link, useNavigate, useParams, useSearchParams, useOutletContext } from 'react-router-dom'
import { useComputed, useSignal, useSignalEffect } from '@preact/signals-react'

import Alert from 'react-bootstrap/Alert'
import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Button from 'react-bootstrap/Button'
import Col from 'react-bootstrap/Col'
import Dropdown from 'react-bootstrap/Dropdown'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'

import ConfirmationModal from '../../components/ConfirmationModal'
import DebugAccordion from '../../components/DebugAccordion'
import Icon from '../../components/Icon.jsx'
import Loadable from '../../components/Loadable'
import LoadingAnimation from '../../components/LoadingAnimation.jsx'
import PageTitle from '../../components/PageTitle.jsx'
import ConfigTemplateNav, { ConfigTemplateSubNav } from './components/ConfigTemplateNav'

import { NotificationTypes, addNotification } from '../../state/actions/notificationActions'
import { fetchConfigTemplate, updateConfigTemplate } from '../../state/actions/configTemplateActions'
import { fetchComponentConfigSchema } from '../../state/actions/componentConfigSchemaActions'
import { fetchSchemaManifest } from '../../state/actions/schemaManifestActions'

import { configTemplateSignal, configTemplateLoadedSignal, configTemplateErrorSignal } from '../../state/signals/configTemplateSignals'
import { componentConfigSchemaSignal, componentConfigSchemaLoadedSignal, componentConfigSchemaErrorSignal } from '../../state/signals/componentConfigSchemaSignals'
import { schemaManifestSignal } from '../../state/signals/schemaManifestSignals'

import { ComponentInterfaceType } from '../../utils/constants.js'
import useRedirectOnBeIdChange from '../../hooks/useRedirectOnBeIdChange.js'

export default function ConfigTemplateApiInterfaceList() {

    const navigate = useNavigate()
    const { beId, configTemplateId } = useParams()
    const beIdChanged = useRedirectOnBeIdChange(beId, `/be/${beId}/templates`)
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { config, accessToken, userPermissionsSummary } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary.value && !userPermissionsSummary.value.canViewConfigTemplates) {
            addNotification('You are not authorized to view config templates for this account.', NotificationTypes.ERROR, true)
            navigate('/')
        }
    }, [userPermissionsSummary.value, navigate])

    const canEditComputed = useComputed(() => userPermissionsSummary?.value?.canEditConfigTemplates)

    useEffect(() => {
        if (beIdChanged) return
        fetchSchemaManifest()
        fetchConfigTemplate(beId, configTemplateId, config, accessToken).then(template => {
            if (template?.utgSchemaVersion) {
                fetchComponentConfigSchema(beId, template.utgSchemaVersion, config, accessToken)
            }
        })
    }, [beId, beIdChanged, configTemplateId, config, accessToken])

    const configTemplateNameSignal = useSignal('')
    const configTemplateVersionSignal = useSignal('')

    useSignalEffect(() => {
        configTemplateNameSignal.value = configTemplateSignal.value?.name ?? ''
        configTemplateVersionSignal.value = configTemplateSignal.value?.utgSchemaVersion ?? ''
    })

    const schemaVersionOptionsComputed = useComputed(() => {
        const configs = schemaManifestSignal.value?.configs
        if (!configs?.length) return []
        return [...configs]
            .sort((a, b) => a.utgVersion < b.utgVersion ? 1 : -1)
            .map(({ utgVersion }) => (
                <option value={utgVersion} key={utgVersion}>{utgVersion}</option>
            ))
    })

    const handleNameSave = (event) => {
        event.preventDefault()
        updateConfigTemplate(beId, { ...configTemplateSignal.value, name: configTemplateNameSignal.value, utgSchemaVersion: configTemplateVersionSignal.value }, config, accessToken)
            .then(result => {
                configTemplateSignal.value = result
                addNotification('Config template updated.', NotificationTypes.SUCCESS, true)
            })
            .catch(error => addNotification(`Unable to update. ${error.toString()}`, NotificationTypes.ERROR, false))
    }

    const parsedConfigComputed = useComputed(() => {
        try { return JSON.parse(configTemplateSignal.value?.config || '{}') } catch { return {} }
    })

    const apiInterfacesComputed = useComputed(() => {
        return parsedConfigComputed.value?.apiInterfaceConfig?.apiInterface ?? []
    })

    const apiInterfaceDropdownItemsComputed = useComputed(() => {
        if (!componentConfigSchemaLoadedSignal.value) return []
        const items = componentConfigSchemaSignal.value?.properties?.config?.properties?.apiInterfaceConfig?.properties?.apiInterface?.items
        if (!items?.length) return []
        return items.map(s => ({
            apiInterfaceName: s.properties.cdType['s4-caption'],
            id: s.properties.cdType['s4-model-key']
        }))
    })

    const showDeleteModalSignal = useSignal(false)
    const confirmTaskDescSignal = useSignal('')
    const confirmCdTypeSignal = useSignal('')

    const accordionData = [
        { name: 'API Interfaces', data: apiInterfacesComputed.value },
        { name: 'Config Template', data: configTemplateSignal.value },
        { name: 'API Interfaces Dropdown data', data: apiInterfaceDropdownItemsComputed.value },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config }
    ]

    function ApiInterfaceTableRows({ apiInterfaces }) {
        if (!configTemplateLoadedSignal.value) {
            return <tr><td colSpan={4}><LoadingAnimation /></td></tr>
        }
        if (apiInterfaces && apiInterfaces.length == 0) {
            return (
                <tr><td colSpan="4" className="text-center">This config template does not have any configured API Interfaces</td></tr>
            )
        }

        let rows = []
        apiInterfaces.forEach(apiInterface => {
            rows.push(
                <ApiInterfaceRow apiInterface={apiInterface} key={`${apiInterface.cdType}${apiInterface.taskDesc}`} />
            )
        })
        return rows
    }

    function ApiInterfaceRow({ apiInterface }) {
        const cdType = apiInterface.cdType
        const taskDesc = apiInterface.taskDesc
        return (
            <tr>
                <td><Link to={`./${cdType}?desc=${encodeURIComponent(taskDesc ?? '')}`}>{taskDesc}</Link></td>
                <td>{cdType}</td>
                <td>{apiInterface.listenPort}</td>
                <td>
                    {canEditComputed.value && <Link to={`./${cdType}?desc=${encodeURIComponent(taskDesc ?? '')}`}>
                        <Button variant="outline-primary" size="sm"><Icon icon="pencil" width="16" height="16"></Icon> Edit</Button>
                    </Link>}
                    {canEditComputed.value && <Button variant="outline-danger" size="sm" className="mx-1" onClick={() => showModal(cdType, taskDesc)}><Icon icon="trash" width="16" height="16"></Icon> Delete</Button>}
                </td>
            </tr>
        )
    }

    const deleteApiInterface = () => {
        showDeleteModalSignal.value = false

        const parsed = parsedConfigComputed.value
        const filtered = (parsed?.apiInterfaceConfig?.apiInterface ?? []).filter(apiInterface =>
            !(apiInterface.cdType == confirmCdTypeSignal.value && apiInterface.taskDesc == confirmTaskDescSignal.value))

        const updatedConfig = {
            ...parsed,
            apiInterfaceConfig: { ...parsed.apiInterfaceConfig, apiInterface: filtered }
        }

        updateConfigTemplate(beId, { ...configTemplateSignal.value, config: JSON.stringify(updatedConfig) }, config, accessToken)
            .then(result => {
                configTemplateSignal.value = result
                addNotification('The Api Interface was successfully removed.', NotificationTypes.SUCCESS, true)
            })
            .catch(error => addNotification(`Unable to remove the API Interface. ${error.toString()}`, NotificationTypes.ERROR, false))
    }

    const showModal = (cdType, taskDesc) => {
        confirmCdTypeSignal.value = cdType
        confirmTaskDescSignal.value = taskDesc
        showDeleteModalSignal.value = true
    }

    function NewApiInterfaceDropdown() {
        // only allow one Oracle Interface per template, matching the UTG page
        const oracleApiInterfaces = apiInterfacesComputed.value.filter(apiInterface =>
            apiInterface.cdType == ComponentInterfaceType.OraclePaymentInterface)

        const apiInterfaceOptions = apiInterfaceDropdownItemsComputed.value.map(s => {
            const isDisabled = s.id == ComponentInterfaceType.OraclePaymentInterface && oracleApiInterfaces.length > 0
            return (
                <Dropdown.Item as={Link} to={`./${s.id}`} key={s.id} disabled={isDisabled}>{s.apiInterfaceName}</Dropdown.Item>
            )
        })

        if (!apiInterfaceOptions.length) return null

        return (
            <DropdownButton id="dropdown-add-apiinterface"
                variant="outline-secondary"
                size="sml"
                title="New API Interface"
                className="mx-3">
                {apiInterfaceOptions}
            </DropdownButton>
        )
    }

    return (
        <>
            {canEditComputed.value && (
                <ConfirmationModal
                    show={showDeleteModalSignal.value}
                    handleCancel={() => showDeleteModalSignal.value = false}
                    handleConfirm={deleteApiInterface}
                    message={`Are you sure you want to delete ${confirmTaskDescSignal.value}? This action cannot be undone.`}
                    title={`Deleting ${confirmTaskDescSignal.value}`} />
            )}
            <Breadcrumb className="px-3 pt-1 bg-light">
                <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/templates` }}>
                    Config Templates
                </Breadcrumb.Item>
                <Breadcrumb.Item active>
                    {configTemplateLoadedSignal.value ? <>Manage {configTemplateSignal.value?.name}</> : <>Loading...</>}
                </Breadcrumb.Item>
            </Breadcrumb>
            <Stack direction="horizontal" gap={3}>
                <PageTitle title="Manage Config Template" canEditSignal={canEditComputed} />
            </Stack>
            <Form className="px-3 mb-2" onSubmit={handleNameSave}>
                <Form.Group as={Row} className="mb-2" controlId="ConfigTemplateNameApiInterfaces">
                    <Form.Label column sm="2" className="text-end">Name:</Form.Label>
                    <Col sm="10">
                        <Form.Control
                            type="text"
                            required
                            value={configTemplateNameSignal.value}
                            onInput={e => configTemplateNameSignal.value = e.target.value}
                            readOnly={!canEditComputed.value}
                        />
                    </Col>
                </Form.Group>
                <Form.Group as={Row} className="mb-2" controlId="ConfigTemplateVersionApiInterfaces">
                    <Form.Label column sm="2" className="text-end">Schema Version:</Form.Label>
                    <Col sm="8">
                        <Form.Control as="select" value={configTemplateVersionSignal.value} onChange={e => configTemplateVersionSignal.value = e.target.value} disabled={!canEditComputed.value}>
                            <option value="">Select Schema Version</option>
                            {schemaVersionOptionsComputed.value}
                        </Form.Control>
                    </Col>
                    {canEditComputed.value && (
                        <Col sm="2">
                            <button type="submit" className="btn btn-primary btn-sm">Save</button>
                        </Col>
                    )}
                </Form.Group>
            </Form>
            <ConfigTemplateNav activeKey="2" />
            <ConfigTemplateSubNav activeKey="3" />
            <Loadable loadedSignals={[configTemplateLoadedSignal]} errorSignals={[configTemplateErrorSignal, componentConfigSchemaErrorSignal]}>
                <Alert variant="info" className="mx-3 py-2 small">
                    API Interfaces in a config template only update interfaces that already exist on the target UTG.
                    An interface is matched on Type and Description; if no match is found, it is skipped when the template is applied.
                </Alert>
                <Table responsive striped hover size="sm">
                    <thead>
                        <tr>
                            <th>Description</th>
                            <th>Type</th>
                            <th>Port</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody><ApiInterfaceTableRows apiInterfaces={apiInterfacesComputed.value} /></tbody>
                </Table>
                {canEditComputed.value && <NewApiInterfaceDropdown />}
            </Loadable>
            <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}
